import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_FLOOR_PLAN_GRID_SIZE,
  floorPlanGridSize,
  type FloorPlanGridSize,
} from '@peles/shared';

interface UsePlanGridSizeOptions {
  /** The plan the canvas is showing, or null for a canvas not saved yet. */
  planId: string | null;
  /** The plan as last fetched — its `gridSizeFt` is the stored size. */
  plan: { id: string; gridSizeFt?: number | null } | undefined;
  /** Store a size on a plan. */
  save: (planId: string, size: FloorPlanGridSize) => Promise<unknown>;
  /**
   * A plan's last size chosen could not be saved. The plan on screen has gone
   * back to its stored size; another plan will show its stored size when it
   * is opened again.
   */
  onSaveFailed: (planId: string, onScreen: boolean) => void;
}

/** One plan's saves: the size on the wire, and the latest chosen meanwhile. */
interface Queue {
  sending: FloorPlanGridSize | null;
  next: FloorPlanGridSize | null;
}

/**
 * A plan's grid size: shown at once when chosen, and saved one write at a
 * time per plan, so the last size chosen is the last one stored — two quick
 * choices never land out of order, and leaving the plan mid-save neither
 * drops a choice still waiting nor lets an older save overtake a newer one.
 * A plan's saves carry on while another plan is open; only the plan on
 * screen is redrawn (going back to its stored size when a save fails), and a
 * failure is reported whichever plan it was for. While nothing of ours is being saved the grid follows the plan as
 * fetched, so a size another editor chose arrives with the next refetch.
 */
export function usePlanGridSize({ planId, plan, save, onSaveFailed }: UsePlanGridSizeOptions) {
  const [size, setSize] = useState<FloorPlanGridSize>(DEFAULT_FLOOR_PLAN_GRID_SIZE);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  // What each plan stores, as last read or saved; and each plan's saves.
  const stored = useRef(new Map<string, FloorPlanGridSize>());
  const queues = useRef(new Map<string, Queue>());
  // How many sizes have been chosen on each plan — a read begun before the
  // latest choice must not replace it.
  const choices = useRef(new Map<string, number>());
  const saveRef = useRef(save);
  saveRef.current = save;
  const failedRef = useRef(onSaveFailed);
  failedRef.current = onSaveFailed;
  const planIdRef = useRef(planId);
  planIdRef.current = planId;

  const queueFor = useCallback((id: string) => {
    let q = queues.current.get(id);
    if (!q) {
      q = { sending: null, next: null };
      queues.current.set(id, q);
    }
    return q;
  }, []);

  const planKey = plan?.id;
  const planSize = plan?.gridSizeFt;
  // A plan chosen but not loaded yet has no stored size to save against or
  // go back to, so its grid cannot be changed until it arrives.
  const ready = !planId || planKey === planId;
  useEffect(() => {
    if (!planKey || planKey !== planId) return;
    const q = queues.current.get(planKey);
    if (q && q.sending !== null) {
      // A size of ours is on its way; the fetched one may predate it. Show
      // what this plan is about to store.
      setSize(q.next ?? q.sending);
      return;
    }
    const server = floorPlanGridSize(planSize);
    stored.current.set(planKey, server);
    setSize(server);
  }, [planKey, planSize, planId]);

  const send = useCallback(
    (forPlan: string, value: FloorPlanGridSize) => {
      const q = queueFor(forPlan);
      q.sending = value;
      const settle = (ok: boolean) => {
        if (ok) stored.current.set(forPlan, value);
        const known = stored.current.get(forPlan);
        const next = q.next;
        q.sending = null;
        q.next = null;
        if (next !== null && next !== known) {
          send(forPlan, next);
          return;
        }
        if (ok || next !== null) return;
        // Failed with nothing chosen since: the last size chosen is lost.
        const onScreen = planIdRef.current === forPlan;
        if (onScreen && known !== undefined) setSize(known);
        failedRef.current(forPlan, onScreen);
      };
      saveRef.current(forPlan, value).then(
        () => settle(true),
        () => settle(false),
      );
    },
    [queueFor],
  );

  /** Show a size and store it on the plan. */
  const choose = useCallback(
    (value: FloorPlanGridSize) => {
      // The size already on screen is no choice: nothing to save, and a
      // reload in flight must still be taken.
      if (!ready || value === sizeRef.current) return;
      setSize(value);
      if (!planId) return; // kept for the plan this canvas becomes
      choices.current.set(planId, (choices.current.get(planId) ?? 0) + 1);
      const q = queueFor(planId);
      if (q.sending !== null) {
        q.next = value;
        return;
      }
      if (stored.current.get(planId) === value) return;
      send(planId, value);
    },
    [planId, ready, queueFor, send],
  );

  /** Call as a read of a plan begins; hand the answer to `adopt` with it. */
  const beginRead = useCallback((forPlan: string) => choices.current.get(forPlan) ?? 0, []);

  /**
   * Take a size just read from the server for a plan — "Load their version".
   * A size chosen on that plan after the read began (`since`, from
   * `beginRead`) is newer, so it stands and the size read is dropped.
   * Otherwise a save of ours still on the wire would land after it, so the
   * size read is sent again behind it, leaving the plan storing what was
   * loaded. It is drawn only if that plan is still the one on screen.
   */
  const adopt = useCallback(
    (forPlan: string, value: number | null | undefined, since?: number) => {
      if (since !== undefined && (choices.current.get(forPlan) ?? 0) !== since) return;
      const server = floorPlanGridSize(value);
      stored.current.set(forPlan, server);
      const q = queueFor(forPlan);
      q.next = q.sending !== null && q.sending !== server ? server : null;
      if (planIdRef.current === forPlan) setSize(server);
    },
    [queueFor],
  );

  return { size, ready, choose, beginRead, adopt };
}
